import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { errorMessage, useCreateCpuWorkspace } from "../../api/queries";
import type {
  Capacity,
  CpuCapacity,
  EnvironmentOption,
} from "../../api/types";
import {
  NO_RESIZE_NOTE,
  WORKSPACE_NEVER_IDLE_NOTE,
  WORKSPACE_RESERVATION_NOTE,
  workspaceCoreChoices,
  workspaceCoreError,
} from "../../lib/cpu";
import { pluralize } from "../../lib/format";

/** Durations offered as one-click presets, exactly as the GPU form offers them. */
const HOUR_PRESETS: readonly number[] = [1, 2, 4, 8, 24];

interface CpuWorkspaceFormProps {
  /** The whole payload: the hours of this kind are the workspace pair. */
  capacity: Capacity;
  cpu: CpuCapacity;
  environments: EnvironmentOption[];
  /** The cluster-wide emergency stop, published by the server. */
  paused: boolean;
  /** Whether a key on file is scoped to workspaces — the only kind that
   *  opens one; a key for training elsewhere does not count. */
  hasSshKey: boolean;
  /**
   * Whether this console also sends its warnings as Slack messages.
   *
   * The deadline is the only thing that ends this kind, so where its warning
   * arrives is part of the deal being agreed to here — and it is the
   * deployment's answer (`me.slack_notifications`), never this file's.
   */
  slackNotifications: boolean;
  /**
   * The size the screen has a reason to open on, when it has one.
   *
   * The terminal card has one: the smallest size **the server** published. It
   * is honoured only where the server still offers it, so it can never be a
   * count this bundle invented; without it the form opens on the first size
   * that starts immediately, as it always has.
   */
  preferredCores?: number | null;
}

/**
 * Three choices, then you have a machine with cores and a shell.
 *
 * The same form as *Get GPUs*, with cores where the GPU count was, and every
 * bound on it is the server's: the counts are
 * `capacity.cpu.defaults.allowed_workspace_cores` — the filtered list
 * `400 invalid_core_count` validates against, so a button here can never be a
 * size the console then refuses — and the hours are the **workspace** pair in
 * the top-level defaults, because a CPU workspace has no duration numbers of
 * its own.
 *
 * The promises this kind carries — what the largest size really is, what a
 * reservation buys, that nothing stops it for being idle, that there is no
 * resize — are four paragraphs, and four paragraphs in front of three
 * questions is a form nobody finishes. Each leaves its lead sentence where it
 * was and the rest sits in one disclosure at the foot of the form: still on
 * the page, still in the words it was written in, one click instead of none.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *     cpu: Its CPU half, already known to be present.
 *     environments: What the workspace may run.
 *     paused: Whether the console is paused.
 *     hasSshKey: Whether a key on file is scoped to workspaces.
 *     preferredCores: A published size to open on, or null.
 *
 * Returns:
 *     The CPU workspace form.
 */
export function CpuWorkspaceForm({
  capacity,
  cpu,
  environments,
  paused,
  hasSshKey,
  slackNotifications,
  preferredCores = null,
}: CpuWorkspaceFormProps): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateCpuWorkspace();

  const choices = workspaceCoreChoices(cpu);
  const preferred = choices.find((choice) => choice.count === preferredCores);
  const defaultCores =
    preferred?.count ??
    choices.find((choice) => choice.immediate)?.count ??
    choices[0]?.count ??
    null;
  const defaultHours = capacity.defaults.workspace_hours;
  const maxHours = capacity.defaults.max_workspace_hours;

  const [cores, setCores] = useState<number | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [custom, setCustom] = useState(false);
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedCores = cores ?? defaultCores;
  const selectedHours = hours ?? defaultHours;
  const selectedImage = image ?? environments[0]?.id ?? "";
  const selected = choices.find((choice) => choice.count === selectedCores);
  // The server's default duration is what an untouched form submits, so it has
  // to be one of the pills and visibly chosen: a deadline nobody agreed to is
  // the one thing that ends this kind.
  const presets = [...new Set([...HOUR_PRESETS, defaultHours])]
    .filter((preset) => preset <= maxHours)
    .sort((a, b) => a - b);

  const submit = (): void => {
    if (selectedCores === null) {
      return;
    }
    // The offer can shrink under a chosen button — a fleet re-keyed to a
    // smaller shape, a machine measured lower than its pool advertises — so
    // the count is checked against the published list one last time, in the
    // sentence the server would have used.
    const refusal = workspaceCoreError(selectedCores, cpu);
    setError(refusal);
    if (refusal !== null) {
      return;
    }
    create.mutate(
      { cores: selectedCores, hours: selectedHours, image: selectedImage },
      { onSuccess: (opened) => navigate(`/cpu-workspaces/${opened.id}`) },
    );
  };

  if (!cpu.measured) {
    // The console does not open a workspace against free-core numbers it could
    // not verify: a count that missed the machines' own services would place
    // it on a machine with no room for it. The server refuses for the same
    // reason, so the form does not offer the click.
    return (
      <p className="state-block">
        The console could not check what is free on the CPU machines just now,
        so it will not open a workspace against numbers it could not verify.
        The reason is beside the machines below; nothing already running is
        affected.
      </p>
    );
  }

  if (choices.length === 0) {
    return (
      <p className="state-block">
        This console offers no CPU workspace size at all right now — no machine
        it can start could hold one. Nothing of yours is affected; ask the
        platform team.
      </p>
    );
  }

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className="form__block">
        <legend className="form__legend">How many cores?</legend>
        <div className="core-choices">
          {choices.map((choice) => (
            <label
              key={choice.count}
              className="core-choice"
              data-selected={selectedCores === choice.count}
            >
              <input
                type="radio"
                name="cores"
                className="sr-only"
                value={choice.count}
                checked={selectedCores === choice.count}
                aria-label={pluralize(choice.count, "core")}
                aria-describedby={`core-choice-${choice.count}`}
                onChange={() => {
                  setCores(choice.count);
                  setError(null);
                }}
              />
              <span className="core-choice__count" aria-hidden="true">
                {choice.count}
                <span className="core-choice__unit">
                  {choice.count === 1 ? "CORE" : "CORES"}
                </span>
              </span>
              <span
                className="core-choice__reason"
                id={`core-choice-${choice.count}`}
              >
                {choice.reason}
              </span>
            </label>
          ))}
        </div>
        {selected && !selected.immediate ? (
          <p className="form__note">
            You can ask anyway: the console starts a machine and your workspace
            opens on its own when it is up.
          </p>
        ) : null}
        {/* The lead sentence only. Why the largest is not the number the
            machine advertises, and what a reservation really buys, are two
            paragraphs — they are in the disclosure at the foot of the form. */}
        <p className="form__note">
          The largest of these is one whole machine, and what you pick is
          yours.
        </p>
      </fieldset>

      <fieldset className="form__block">
        <legend className="form__legend">For how long?</legend>
        <div className="choice-row">
          {presets.map((preset) => (
            <label
              key={preset}
              className="choice"
              data-selected={!custom && selectedHours === preset}
            >
              <input
                type="radio"
                name="cpu-workspace-hours"
                className="sr-only"
                value={preset}
                checked={!custom && selectedHours === preset}
                onChange={() => {
                  setCustom(false);
                  setHours(preset);
                }}
              />
              {preset} h
            </label>
          ))}
          <label className="choice" data-selected={custom}>
            <input
              type="radio"
              name="cpu-workspace-hours"
              className="sr-only"
              checked={custom}
              onChange={() => setCustom(true)}
            />
            Custom
          </label>
        </div>
        {custom ? (
          <p className="form__custom">
            <label htmlFor="cpu-workspace-custom-hours">Hours</label>
            <input
              id="cpu-workspace-custom-hours"
              className="input input--narrow"
              type="number"
              min={1}
              max={maxHours}
              value={selectedHours}
              onChange={(event) => setHours(Number(event.target.value))}
            />
            <span className="field__hint">1 to {maxHours} hours.</span>
          </p>
        ) : null}
        <p className="form__note">
          You can always add more time later, in one click, and you are warned
          before the deadline arrives
          {slackNotifications ? " — on this screen and on Slack" : ""}. Nothing
          else ends this kind — an idle workspace bills to its deadline, so set
          the deadline you mean.
        </p>
      </fieldset>

      <fieldset className="form__block">
        <legend className="form__legend">Which environment?</legend>
        {environments.length === 0 ? (
          <p className="form__note">
            The server published no environment, so there is nothing to run.
          </p>
        ) : (
          <ul className="image-choices">
            {environments.map((option) => (
              <li key={option.id}>
                <label
                  className="image-choice"
                  data-selected={selectedImage === option.id}
                >
                  <input
                    type="radio"
                    name="cpu-workspace-image"
                    className="sr-only"
                    value={option.id}
                    checked={selectedImage === option.id}
                    aria-label={option.name}
                    onChange={() => setImage(option.id)}
                  />
                  <span className="image-choice__name">{option.name}</span>
                  <span className="image-choice__description">
                    {option.description ?? ""}
                    {option.added_by ? (
                      <span className="image-choice__owner">
                        {option.description ? " — " : ""}added by{" "}
                        {option.added_by}
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      {/* Four paragraphs that are all true and none of which answer "how many
          cores, for how long, running what". They are one click away, in the
          words they were written in — shortened on the screen, never on the
          page. */}
      <details className="form__more">
        <summary>What a CPU workspace promises</summary>
        <div className="form__more-body">
          <p className="form__note">
            The largest size offered is one machine — a little under what the
            machine advertises, because it keeps some of itself for itself and
            because memory has to be able to feed the cores. It is the size of
            a machine, not a ration: nobody here has cores of their own.
          </p>
          <p className="form__note">{WORKSPACE_RESERVATION_NOTE}</p>
          <p className="form__note">{WORKSPACE_NEVER_IDLE_NOTE}</p>
          <p className="form__note">{NO_RESIZE_NOTE}</p>
        </div>
      </details>

      {!hasSshKey ? (
        <p className="form__error" role="status">
          You need an SSH key for your workspaces first — it is the only way
          into one. Add a key on <Link to="/account">your account screen</Link>,
          or widen one you already have to cover your workspaces.
        </p>
      ) : null}

      {paused ? (
        <p className="form__error" role="status">
          The console is paused right now — no workspace can start. Work already
          running is untouched.
        </p>
      ) : null}

      {error !== null ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {create.isError ? (
        // The server's refusals are written for this reader and name what to
        // do — the workload holding your files, the size of a machine, the
        // fleet's ceiling. They are shown exactly as they were written.
        <p className="form__error" role="alert">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn btn--primary btn--hero"
        disabled={
          create.isPending ||
          paused ||
          !hasSshKey ||
          environments.length === 0 ||
          selectedCores === null
        }
      >
        {create.isPending ? "Starting…" : "Start workspace"}
      </button>
    </form>
  );
}
